// ─────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for every campaign.
//
// Imported by BOTH:
//   • the server  — ensureCampaigns.ts uses slug/name/offer/cta to seed rows
//   • the client  — CampaignHero.tsx uses emoji/colours/effects for the look
//
// TO ADD A NEW CAMPAIGN: add one entry below. On the next server start its
// row is auto-seeded (disabled); it appears in /admin/campaigns automatically;
// and the hero themes itself from the look fields here. You only touch
// CampaignHero.tsx if you need a brand-new effect type (a new `effects` flag).
// ─────────────────────────────────────────────────────────────────

export interface CampaignDef {
  // Identity + seed defaults (server)
  slug: string;
  name: string;
  offerText: string;
  ctaLabel: string;
  ctaLink: string;
  // Look (client)
  emoji: string;
  /** rgba() for the warm background glow. */
  glowColor: string;
  /** Sparkle / light particle colour. */
  sparkleColor: string;
  /** Gradient used for the offer chip border + CTA button. */
  gradientFrom: string;
  gradientTo: string;
  /** Bulb colours for the string-lights effect (cycled across the string). */
  lightColors?: string[];
  /** When set, the hero background is fully replaced with a radial wash
   *  (warm `center` fading to deep `edge`) + SVG rangoli ornaments. */
  takeover?: { center: string; mid: string; edge: string };
  effects: {
    sparkles?: boolean;
    diyas?: boolean;
    flyingLamps?: boolean;
    snow?: boolean;
    fireworks?: boolean;
    lights?: boolean;
    rockets?: boolean;
    anaars?: boolean;
    marigold?: boolean;
    santa?: boolean;
    confetti?: boolean;
    saleTags?: boolean;
  };
}

export const campaignPresets: CampaignDef[] = [
  {
    slug: "diwali",
    name: "Diwali",
    offerText: "Light up the festive season with ZeroSmoke",
    ctaLabel: "Shop Diwali Offers",
    ctaLink: "/store",
    emoji: "🪔",
    glowColor: "rgba(245, 158, 11, 0.30)",
    sparkleColor: "#fbbf24",
    gradientFrom: "#f59e0b",
    gradientTo: "#ef4444",
    lightColors: ["#f59e0b", "#ef4444", "#22c55e", "#3b82f6", "#eab308"],
    takeover: { center: "#f59e0b", mid: "#9a3412", edge: "#1e0a28" },
    effects: { rockets: true, diyas: true, flyingLamps: true, lights: true },
  },
  {
    slug: "christmas",
    name: "Christmas",
    offerText: "Merry Christmas — festive picks for the season",
    ctaLabel: "Shop Christmas",
    ctaLink: "/store",
    emoji: "🎄",
    glowColor: "rgba(239, 68, 68, 0.22)",
    sparkleColor: "#fca5a5",
    gradientFrom: "#ef4444",
    gradientTo: "#22c55e",
    lightColors: ["#ef4444", "#22c55e", "#fde047", "#ffffff"],
    effects: { snow: true, santa: true, lights: true },
  },
  {
    slug: "new-year",
    name: "New Year",
    offerText: "New year, fresh stock — ring it in with ZeroSmoke",
    ctaLabel: "Shop New Year",
    ctaLink: "/store",
    emoji: "🎆",
    glowColor: "rgba(168, 85, 247, 0.24)",
    sparkleColor: "#d8b4fe",
    gradientFrom: "#6366f1",
    gradientTo: "#a855f7",
    lightColors: ["#a855f7", "#6366f1", "#fde047"],
    effects: { fireworks: true, confetti: true, sparkles: true, lights: true },
  },
  {
    slug: "sale",
    name: "Mega Sale",
    offerText: "Mega Sale is live — limited-time prices",
    ctaLabel: "Shop the Sale",
    ctaLink: "/store",
    emoji: "🏷️",
    glowColor: "rgba(245, 158, 11, 0.24)",
    sparkleColor: "#fcd34d",
    gradientFrom: "#f59e0b",
    gradientTo: "#dc2626",
    lightColors: ["#f59e0b", "#dc2626", "#fde047"],
    effects: { confetti: true, saleTags: true, sparkles: true },
  },
  {
    slug: "new-drop",
    name: "New Drop",
    offerText: "Fresh drop just landed — shop the latest arrivals",
    ctaLabel: "Shop New Arrivals",
    ctaLink: "/store",
    emoji: "🔥",
    glowColor: "rgba(139, 92, 246, 0.24)",
    sparkleColor: "#67e8f9",
    gradientFrom: "#06b6d4",
    gradientTo: "#8b5cf6",
    lightColors: ["#06b6d4", "#8b5cf6", "#ffffff"],
    effects: { sparkles: true, confetti: true, lights: true },
  },
];

export const campaignSlugs = campaignPresets.map((c) => c.slug);

/** Look/config for a campaign slug, or null if the slug is unknown. */
export function getCampaignTheme(slug: string): CampaignDef | null {
  return campaignPresets.find((c) => c.slug === slug) ?? null;
}
